import clsx from "clsx";
import { ArrowRight, Check, X } from "lucide-react";
import { useId, useState } from "react";

import type { Exercise } from "../../api/types";
import { Disclosure, DisclosureChevron } from "../../components/panel";
import { motion, usePresence } from "../../components/presence";
import { Button, IconButton } from "../../components/ui";
import { k, t } from "../../i18n/i18n";
import { isComplete } from "./dossier";
import { firstStepsHidden, hideFirstSteps, phasesTried } from "./firstStepsState";

/** Premiers pas (prise en main étoffée, demande d'Alexis du 01/10/2026) : le parcours d'un exercice en huit
 *  étapes (« Construire sa manœuvre » ajoutée avec la manœuvre libre, docs/14 §2.12), cochées d'elles-mêmes d'après les dossiers de la licence (et, pour les temps de la carte, ce poste) ;
 *  la prochaine étape est désignée, avec son bouton. Masquable ; disparaît d'elle-même une fois le parcours
 *  achevé. Pastilles d'étape au style de la frise « Avancement » de l'accueil ; carte posée (`shadow-card`). */

export type StepTarget = "create" | "mission" | "map" | "manoeuvre" | "briefing" | "order" | "direx" | "aar";

interface StepState {
  key: string;
  label: string;
  hint: string;
  done: boolean;
  target: StepTarget;
}

export function firstSteps(exercises: Exercise[]): StepState[] {
  const any = (test: (ex: Exercise) => boolean) => exercises.some(test);
  return [
    {
      key: "create",
      label: k("Créer un dossier"),
      hint: k("Nouveau dossier, ou dossier d'exemple pour découvrir sans rien saisir."),
      done: exercises.length > 0,
      target: "create",
    },
    {
      key: "mission",
      label: k("Compléter les Données initiales"),
      hint: k("Niveau, force, mission, ennemi, camp ou terrain libre : le dispositif se place sur la carte."),
      done: any(isComplete),
      target: "mission",
    },
    {
      key: "map",
      label: k("Parcourir T1, T2, T3 sur la carte"),
      hint: k("Les unités glissent d'un temps à l'autre avec leurs symboles de mission."),
      // Changement de temps fait sur ce poste, ou déjà un ordre rédigé : la carte est connue (utilisateurs d'avant
      // les premiers pas compris).
      done: phasesTried() || any((ex) => (ex.orders?.count ?? 0) > 0),
      target: "map",
    },
    {
      key: "manoeuvre",
      label: k("Construire sa manœuvre"),
      hint: k("Vue Manœuvre : retouchez le fuseau ou choisissez la mission d'une unité."),
      // Fuseau retouché, LIMA ou ligne de la ZO déplacée, ou mission choisie dans l'un des dossiers (champ `progress`).
      done: any((ex) => Boolean(ex.progress?.manoeuvre)),
      target: "manoeuvre",
    },
    {
      key: "briefing",
      label: k("Rédiger la mise en ambiance"),
      hint: k("Préambule et situations, rédigés par l'IA locale d'après le dossier."),
      done: any((ex) => Boolean(ex.progress?.briefing)),
      target: "briefing",
    },
    {
      key: "order",
      label: k("Rédiger l'ordre d'opérations"),
      hint: k("Généré d'après la carte, puis relu, validé et exporté."),
      done: any((ex) => (ex.orders?.count ?? 0) > 0),
      target: "order",
    },
    {
      key: "direx",
      label: k("Préparer la DIREX"),
      hint: k("Incidents MEL/MIL générés, datés et prêts à injecter."),
      done: any((ex) => (ex.progress?.incidents ?? 0) > 0),
      target: "direx",
    },
    {
      key: "aar",
      label: k("Évaluer en 3A"),
      hint: k("Notez les incidents : l'IA propose l'aide rédactionnelle, l'EVALOPS sort en PDF."),
      done: any((ex) => (ex.progress?.evaluated ?? 0) > 0),
      target: "aar",
    },
  ];
}

/** `secondary` : un autre appel principal est déjà à l'écran (« Reprendre ») ; « Faire » passe alors en bouton
 *  secondaire, toujours pulsant, pour qu'il n'y ait qu'un bouton noir par écran (R5). */
export function FirstSteps({
  exercises,
  onStep,
  secondary = false,
}: {
  exercises: Exercise[];
  onStep: (target: StepTarget) => void;
  secondary?: boolean;
}) {
  const [hidden, setHidden] = useState(firstStepsHidden);
  const [open, setOpen] = useState(exercises.length < 3);
  const listId = useId();
  // Masqués : la carte s'efface en fondu avant de quitter l'accueil.
  const { mounted, closing } = usePresence(!hidden);
  const list = firstSteps(exercises);
  const done = list.filter((s) => s.done).length;
  const next = list.find((s) => !s.done);
  if (!mounted || !next) return null;
  const hide = () => {
    hideFirstSteps();
    setHidden(true);
  };
  return (
    <section className={clsx(motion("fade", closing), "mt-8 rounded-xl border border-line bg-surface shadow-card")} aria-label={t("Premiers pas")}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h2 className="text-title font-semibold text-ink">{t("Premiers pas")}</h2>
            <span className="text-meta text-ink-muted tabular-nums">{t("{done} sur {total}", { done, total: list.length })}</span>
          </div>
          <div className="mt-2 h-1.5 w-full max-w-[360px] overflow-hidden rounded-full bg-line" aria-hidden>
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-(--gx-enter) motion-reduce:transition-none"
              style={{ width: `${(done / list.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0 text-right max-sm:hidden">
            <div className="truncate text-ui font-medium text-ink">{t("Prochaine étape : {step}", { step: t(next.label) })}</div>
            <div className="truncate text-meta text-ink-muted">{t(next.hint)}</div>
          </div>
          <Button highlight variant={secondary ? "secondary" : "primary"} onClick={() => onStep(next.target)}>
            {t("Faire")} <ArrowRight size={14} />
          </Button>
          <div className="flex items-center gap-0.5">
            <IconButton
              size="sm"
              onClick={() => setOpen((o) => !o)}
              title={open ? t("Replier") : t("Voir toutes les étapes")}
              aria-label={open ? t("Replier") : t("Voir toutes les étapes")}
              aria-expanded={open}
              aria-controls={listId}
            >
              <DisclosureChevron open={open} />
            </IconButton>
            <IconButton size="sm" onClick={hide} title={t("Masquer les premiers pas")} aria-label={t("Masquer les premiers pas")}>
              <X size={16} />
            </IconButton>
          </div>
        </div>
      </div>
      <Disclosure open={open} id={listId}>
        <div className="border-t border-line px-5 py-3">
          <ol className="grid gap-x-6 md:grid-cols-2">
            {list.map((step, i) => (
              <li key={step.key} className="flex min-h-8 items-center gap-3 py-1.5">
                <span
                  className={clsx(
                    "inline-flex size-5 shrink-0 items-center justify-center rounded-full text-caps font-semibold tabular-nums",
                    step.done ? "bg-ok text-white" : step === next ? "bg-accent text-white" : "bg-surface text-ink-muted ring-1 ring-line-strong",
                  )}
                >
                  {step.done ? <Check size={12} strokeWidth={3} /> : i + 1}
                </span>
                <span className={clsx("min-w-0 flex-1 truncate text-ui", step.done ? "text-ink-muted line-through decoration-line-strong" : "text-ink")}>
                  {t(step.label)}
                </span>
                {!step.done && step.key !== next.key && (
                  <Button variant="link" className="shrink-0" onClick={() => onStep(step.target)}>
                    {t("Faire")}
                  </Button>
                )}
              </li>
            ))}
          </ol>
        </div>
      </Disclosure>
    </section>
  );
}
